import { ITaskRawQuery } from '@models/Task.model';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useToast } from '@hooks';
import { useMutation, useQueryClient } from 'react-query';
import { patchTask } from '@services/queries/tasks';
import {
  Container,
  Dialog,
  Icon,
  Label,
  Option,
  OptionText,
  Text,
} from './TaskPriorityFlag.styles';

interface ITaskPriorityFlag {
  task: ITaskRawQuery;
  withText?: boolean;
  disabledDialog?: boolean;
}

const TaskPriorityFlag = ({
  task,
  withText,
  disabledDialog,
}: ITaskPriorityFlag) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation(patchTask, {
    onSuccess: async () => {
      toast.success('Task changed successfully');

      queryClient.invalidateQueries(['tasks']);
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const getTaskPriorityFlagColor = (): string => {
    if (task.priority === 'important') return '#FF9900';
    if (task.priority === 'non-critical') return '#52BD94';
    if (task.priority === 'urgent') return '#D14343';

    return '#C1C4D6';
  };

  const getText = (): string => {
    if (task.priority === 'important') return 'Important';
    if (task.priority === 'non-critical') return 'Non-Critical';
    if (task.priority === 'urgent') return 'Urgent!';

    return 'Uncertain';
  };

  return (
    <Container onClick={e => e.stopPropagation()}>
      <Dialog
        menuButton={
          <Label>
            <Icon>
              <BlueIcon icon="flag" color={getTaskPriorityFlagColor()} />
            </Icon>
            {withText && <Text>{getText()}</Text>}
          </Label>
        }
        align="center"
      >
        <Option
          onClick={() => {
            if (task.priority !== 'urgent' && !disabledDialog) {
              mutation.mutate({
                taskId: task.id,
                priority: 'urgent',
              });
            }
          }}
        >
          <Icon>
            <BlueIcon icon="flag" color="#D14343" />
          </Icon>
          <OptionText>Urgent!</OptionText>
        </Option>
        <Option
          onClick={() => {
            if (task.priority !== 'important' && !disabledDialog) {
              mutation.mutate({
                taskId: task.id,
                priority: 'important',
              });
            }
          }}
        >
          <Icon>
            <BlueIcon icon="flag" color="#FF9900" />
          </Icon>
          <OptionText>Important</OptionText>
        </Option>
        <Option
          onClick={() => {
            if (task.priority !== 'non-critical' && !disabledDialog) {
              mutation.mutate({
                taskId: task.id,
                priority: 'non-critical',
              });
            }
          }}
        >
          <Icon>
            <BlueIcon icon="flag" color="#52BD94" />
          </Icon>
          <OptionText>Non-Critical</OptionText>
        </Option>
        <Option
          onClick={() => {
            if (task.priority !== 'uncertain' && !disabledDialog) {
              mutation.mutate({
                taskId: task.id,
                priority: 'uncertain',
              });
            }
          }}
        >
          <Icon>
            <BlueIcon icon="flag" color="#C1C4D6" />
          </Icon>
          <OptionText>Uncertain</OptionText>
        </Option>
      </Dialog>
    </Container>
  );
};

export { TaskPriorityFlag };
